import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { openShopConversation, sendShopMessage, askShopAi, fetchWaMessages } from "../lib/api.js";

// 💬 聯絡店舖 — the customer portal's chat with the shop.
//
// One conversation per customer per shop (the same thread the iOS customer
// app uses), landing in the shop's 訊息 inbox next to its WhatsApp chats.
// After each message we ask the shop's AI to answer; the server decides
// whether it may (platform switch, shop AI switch + hours, plan, monthly cap,
// staff takeover) and otherwise stays quiet so staff reply. Replies show up
// here — polled every 4s while the panel is open.

const POLL_MS = 4000;

export default function CustomerShopChat({ tenantId, shopName, T }) {
  const t = T || ((zh) => zh);
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [waitingAi, setWaitingAi] = useState(false);
  const [err, setErr] = useState("");
  const bottomRef = useRef(null);

  // Open (or create) the conversation the first time the panel opens.
  useEffect(() => {
    if (!open || convId || !tenantId) return;
    let alive = true;
    openShopConversation(tenantId)
      .then((id) => { if (alive) setConvId(id); })
      .catch((e) => { if (alive) setErr(e?.message || t("未能開啟對話", "Couldn't open the chat")); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId]);

  // Load + poll while open.
  useEffect(() => {
    if (!open || !convId) return;
    let alive = true;
    const load = () => fetchWaMessages(convId)
      .then((rows) => { if (alive) setMsgs((prev) => mergeKeepPending(prev, rows)); })
      .catch(() => { /* next tick retries */ });
    load();
    const id = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [open, convId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [msgs, waitingAi, open]);

  const send = async () => {
    const body = text.trim();
    if (!body || !convId || sending) return;
    setText(""); setErr(""); setSending(true);
    const tmpId = "tmp-" + Date.now();
    setMsgs((m) => [...m, { id: tmpId, direction: "in", sender: "customer", body, createdAt: new Date().toISOString(), pending: true }]);
    try {
      await sendShopMessage(convId, body);
    } catch (e) {
      setMsgs((m) => m.filter((x) => x.id !== tmpId));
      setText(body);
      setErr(e?.message || t("發送失敗，請再試", "Send failed — please try again"));
      setSending(false);
      return;
    }
    setSending(false);
    // Ask the AI; it may decline (staff will answer). Either way refresh.
    setWaitingAi(true);
    try {
      const r = await askShopAi(convId);
      if (r?.replied) setMsgs(await fetchWaMessages(convId));
    } catch { /* staff will reply; the poll picks it up */ }
    finally { setWaitingAi(false); }
  };

  if (!tenantId) return null;
  return (
    <>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} aria-label={t("聯絡店舖", "Message the shop")}
          title={t("聯絡店舖", "Message the shop")}
          style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(255,255,255,0.25)", backdropFilter: "blur(10px)", color: "#fff", border: "none", borderRadius: 99,
            cursor: "pointer", flexShrink: 0 }}>
          <MessageCircle size={22} color="#fff" strokeWidth={2.5} />
        </button>
      )}
      {open && (
        <div role="dialog" aria-label={t("聯絡店舖", "Message the shop")}
          style={{ position: "fixed", zIndex: 300, right: 0, bottom: 0, width: "min(420px, 100vw)", height: "min(640px, 100dvh)",
            display: "flex", flexDirection: "column", background: "#ECE5DD", borderRadius: "20px 20px 0 0",
            boxShadow: "0 -10px 40px rgba(0,0,0,.25)", overflow: "hidden" }}>
          <div style={{ background: "#008069", color: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🐾</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 900, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{shopName || t("店舖", "Shop")}</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{waitingAi ? t("輸入中…", "typing…") : t("店舖會喺呢度回覆你", "The shop replies here")}</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label={t("關閉", "Close")}
              style={{ background: "none", border: "none", color: "#fff", fontSize: 24, cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "12px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
            {msgs.length === 0 && !err && (
              <div style={{ alignSelf: "center", background: "#FFF8D6", color: "#5B4A1A", fontSize: 12.5, fontWeight: 700, borderRadius: 10, padding: "8px 12px", textAlign: "center", lineHeight: 1.5 }}>
                {t("想約時間、問價錢或者有其他問題？直接喺度問店舖 😊", "Want to book, ask a price or anything else? Ask the shop here 😊")}
              </div>
            )}
            {msgs.map((m) => <Bubble key={m.id} m={m} t={t} />)}
            {waitingAi && (
              <div style={{ alignSelf: "flex-start", background: "#fff", borderRadius: 12, padding: "9px 14px", color: "#888", fontSize: 18, letterSpacing: 3 }}>• • •</div>
            )}
            <div ref={bottomRef} />
          </div>

          {err && <div style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 12.5, fontWeight: 800, padding: "6px 12px" }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, padding: "10px 10px calc(10px + env(safe-area-inset-bottom))", background: "#F0F2F5" }}>
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={convId ? t("輸入訊息…", "Type a message…") : t("連接中…", "Connecting…")} disabled={!convId}
              style={{ flex: 1, minWidth: 0, border: "none", borderRadius: 99, padding: "11px 16px", fontSize: 15, outline: "none", background: "#fff" }} />
            <button type="button" onClick={send} disabled={!text.trim() || !convId || sending} aria-label={t("傳送", "Send")}
              style={{ width: 44, height: 44, borderRadius: "50%", border: "none", background: "#00A884", color: "#fff", fontSize: 18, cursor: "pointer", opacity: (!text.trim() || !convId || sending) ? 0.5 : 1 }}>➤</button>
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ m, t }) {
  const mine = m.direction === "in";               // the customer's own message
  const ai = !mine && m.sender === "ai";
  const time = m.createdAt ? new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  const isImg = m.mediaUrl && /^image\//.test(m.mediaType || "image/");
  return (
    <div style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "82%",
      background: mine ? "#D9FDD3" : ai ? "#EAE0F7" : "#fff", borderRadius: 12,
      [mine ? "borderTopRightRadius" : "borderTopLeftRadius"]: 3,
      padding: "8px 11px 6px", fontSize: 14.5, lineHeight: 1.45, color: "#111", boxShadow: "0 1px 1px rgba(0,0,0,.1)",
      whiteSpace: "pre-wrap", wordBreak: "break-word", opacity: m.pending ? 0.6 : 1 }}>
      {ai && <div style={{ fontSize: 11, fontWeight: 900, color: "#7C3AED", marginBottom: 2 }}>🤖 {t("AI 助手", "AI assistant")}</div>}
      {isImg && <img src={m.mediaUrl} alt="" style={{ maxWidth: "100%", borderRadius: 8, display: "block", marginBottom: m.body ? 6 : 0 }} />}
      {m.body}
      <div style={{ textAlign: "right", fontSize: 10.5, color: "#8a8a8a", marginTop: 2 }}>{time}</div>
    </div>
  );
}

// Keep optimistic bubbles until the server row with the same text arrives.
function mergeKeepPending(prev, server) {
  const pending = prev.filter((x) => x.pending && !server.some((s) => s.direction === "in" && s.body === x.body));
  return [...server, ...pending];
}
